import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  FaShieldAlt,
  FaDollarSign,
  FaCheckCircle,
  FaClock,
  FaChild,
  FaHome,
  FaUmbrella,
} from "react-icons/fa";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

// FAQ data + item component
const FAQS = [
  {
    q: "How much coverage do I need?",
    a: "A common starting point is 10–12× your annual income, plus major debts and future expenses (like college). We’ll tailor it with you.",
  },
  {
    q: "Can I convert to permanent later?",
    a: "Many term policies include a conversion option within a set window—ask us about carriers and timelines.",
  },
  {
    q: "Do rates change during the term?",
    a: "No. Your premium is locked for the term you choose.",
  },
];

function FAQItem({ q, a, i, openIndex, setOpenIndex }) {
  const isOpen = openIndex === i;
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpenIndex(isOpen ? null : i)}
        className="w-full text-left px-5 py-4 font-semibold flex items-center justify-between"
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${i}`}
      >
        <span className="pr-6">{q}</span>
        <span
          className={`ml-4 inline-block transform transition-transform duration-200 ${
            isOpen ? "rotate-45" : ""
          }`}
          aria-hidden="true"
        >
          +
        </span>
      </button>

      <motion.div
        id={`faq-panel-${i}`}
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        className="overflow-hidden px-5"
      >
        <div className="pb-5 text-gray-600">{a}</div>
      </motion.div>
    </div>
  );
}

export default function TermLife() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <>
      <Helmet>
        <title>Term Life Insurance | Your Choice Associates</title>
        <meta
          name="description"
          content="Affordable, time‑bound coverage that protects what matters most. See term lengths, who it’s for, and how it compares to permanent life insurance."
        />
        <link rel="canonical" href="https://yourchoiceassociates.org/term-life" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Term Life Insurance" />
        <meta
          property="og:description"
          content="Lock in affordable protection for 10–30 years. Family, mortgage, and income protection with flexible coverage amounts."
        />
        <meta property="og:url" content="https://yourchoiceassociates.org/term-life" />
        <meta property="og:site_name" content="Your Choice Associates" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#002b5c] to-[#001b35] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <motion.div {...fade} className="space-y-5">
            <span className="inline-block text-xs font-semibold tracking-wider uppercase bg-white/10 px-3 py-1 rounded-full">
              Life Insurance
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
              Term Life Insurance
            </h1>
            <p className="text-white/90">
              Straightforward, budget‑friendly coverage for a set period—great for protecting
              your income, mortgage, or young family while costs are highest.
            </p>
            <div className="flex gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center px-5 py-3 rounded-xl bg-white text-[#002b5c] font-semibold shadow hover:shadow-md transition"
              >
                Get a Quote
              </Link>
              <a
                href="#details"
                className="inline-flex items-center px-5 py-3 rounded-xl border border-white/30 text-white font-semibold hover:bg-white/10 transition"
              >
                See Details
              </a>
            </div>
          </motion.div>

          <motion.div {...fade} className="bg-white/5 rounded-2xl p-6 md:p-8">
            <ul className="space-y-4">
              {[
                { icon: <FaDollarSign />, t: "Lower premiums than permanent life" },
                { icon: <FaClock />, t: "Choose 10, 15, 20, 25, or 30‑year terms" },
                { icon: <FaHome />, t: "Popular for mortgage + income replacement" },
                { icon: <FaChild />, t: "Protection through kids’ school years" },
              ].map((i, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="mt-1">{i.icon}</div>
                  <p>{i.t}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Who it's for */}
      <section id="details" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-8">
            Is Term Life right for me?
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <FaShieldAlt className="text-[#002b5c] text-2xl" />,
                title: "Income Protection",
                desc: "Replace several years of income so your family’s lifestyle isn’t interrupted.",
              },
              {
                icon: <FaHome className="text-[#002b5c] text-2xl" />,
                title: "Mortgage & Debts",
                desc: "Cover large obligations so your loved ones can stay in the home you built.",
              },
              {
                icon: <FaChild className="text-[#002b5c] text-2xl" />,
                title: "Young Families",
                desc: "Affordable coverage while kids are dependent and expenses are higher.",
              },
            ].map((c, i) => (
              <motion.div
                key={i}
                {...fade}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="mb-3">{c.icon}</div>
                <h3 className="font-bold text-lg mb-2">{c.title}</h3>
                <p className="text-gray-600">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-[#f7fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-10">
            How Term Life works
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div {...fade} className="bg-white p-6 rounded-2xl shadow-sm border">
              <h3 className="font-semibold text-lg mb-3">Locked‑in term & premium</h3>
              <p className="text-gray-600">
                Your premium is fixed for the chosen term (e.g., 20 years). If you pass during
                the term, your beneficiary receives the tax‑free death benefit.
              </p>
            </motion.div>
            <motion.div {...fade} className="bg-white p-6 rounded-2xl shadow-sm border">
              <h3 className="font-semibold text-lg mb-3">Options at the end</h3>
              <p className="text-gray-600">
                When the term ends, you can re‑apply, convert to a permanent policy (if available),
                or let coverage end if you no longer need it.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Term vs Permanent quick compare */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-8">
            Term vs. Permanent (at a glance)
          </motion.h2>
          <div className="overflow-x-auto rounded-2xl border">
            <table className="min-w-full bg-white text-left">
              <thead className="bg-[#002b5c] text-white">
                <tr>
                  <th className="px-5 py-3 font-semibold">Feature</th>
                  <th className="px-5 py-3 font-semibold">Term Life</th>
                  <th className="px-5 py-3 font-semibold">Permanent Life</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Coverage Length", "10–30 years fixed", "Lifelong"],
                  ["Premiums", "Lower", "Higher"],
                  ["Cash Value", "No", "Yes (grows over time)"],
                  ["Best For", "Income/mortgage years", "Legacy & lifelong needs"],
                ].map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-5 py-4 font-medium">{row[0]}</td>
                    <td className="px-5 py-4">{row[1]}</td>
                    <td className="px-5 py-4">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6">
            <Link
              to="/permanent-life"
              className="inline-flex items-center gap-2 text-[#002b5c] font-semibold hover:underline"
            >
              Learn about Permanent Life →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs (fixed accordion) */}
      <section className="py-16 md:py-24 bg-[#f7fafc]">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-8">
            Frequently Asked Questions
          </motion.h2>

          <div className="space-y-4">
            {FAQS.map((item, i) => (
              <FAQItem
                key={i}
                i={i}
                q={item.q}
                a={item.a}
                openIndex={openIndex}
                setOpenIndex={setOpenIndex}
              />
            ))}
          </div>

          <div className="mt-8">
            <Link
              to="/start"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#e31c3d] text-white font-semibold shadow hover:shadow-md transition"
            >
              Speak with a Licensed Agent
            </Link>
          </div>
        </div>
      </section>

      {/* Trust/CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[#002b5c] font-semibold">
            <FaCheckCircle /> Licensed & experienced agents • No obligation quotes
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
